import {
  useCallback,
  type Dispatch,
  type SetStateAction,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getLocalStorageItem,
  localStorageIsSupported,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "simplydo/core/helpers";

export type CachedStateKey = {
  kind: string;
  forType: string;
  forId: string;
  user?: string;
  extra?: Record<string, string>;
};

export type CachedStateIndexEntry = CachedStateKey & {
  storageKey: string;
  updatedAt: number;
};

type CachedStateFilter = Partial<CachedStateKey>;

const STORAGE_PREFIX = "sdo-cache";
const INDEX_STORAGE_KEY = `${STORAGE_PREFIX}:index`;

const isEmptyishString = (value: unknown) => {
  return (
    typeof value === "string" &&
    (value.trim().length === 0 ||
      value === "<p></p>" ||
      value === "<p><br></p>")
  );
};

// Recursively treat values as "empty-ish" so we can drop localStorage keys
// once a draft is effectively cleared.
const isEmptyish = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (value === false) return true;

  if (typeof value === "number") return value === 0;
  if (isEmptyishString(value)) return true;

  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => isEmptyish(item));
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return true;
    return entries.every(([, v]) => isEmptyish(v));
  }

  return false;
};

const safeParseJson = <T,>(value: string): T | undefined => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const normalizeExtra = (extra?: Record<string, string>) => {
  if (!extra) return undefined;

  const entries = Object.entries(extra)
    .filter(([, value]) => !!value)
    .sort(([a], [b]) => a.localeCompare(b));

  if (!entries.length) return undefined;

  return Object.fromEntries(entries);
};

const normalizeCacheKey = (key: CachedStateKey): CachedStateKey => {
  const extra = normalizeExtra(key.extra);

  return {
    kind: key.kind,
    forType: key.forType,
    forId: key.forId,
    ...(key.user ? { user: key.user } : {}),
    ...(extra ? { extra } : {}),
  };
};

export const getCachedStateStorageKey = (key: CachedStateKey): string => {
  const normalized = normalizeCacheKey(key);
  return `${STORAGE_PREFIX}:${encodeURIComponent(JSON.stringify(normalized))}`;
};

const matchesCacheFilter = (
  entry: CachedStateIndexEntry,
  filter?: CachedStateFilter,
) => {
  if (!filter) return true;

  if (filter.kind && entry.kind !== filter.kind) return false;
  if (filter.forType && entry.forType !== filter.forType) return false;
  if (filter.forId && entry.forId !== filter.forId) return false;
  if (filter.user && entry.user !== filter.user) return false;

  if (!filter.extra) return true;

  return Object.entries(filter.extra).every(
    ([extraKey, extraValue]) => entry.extra?.[extraKey] === extraValue,
  );
};

const getCachedStateIndex = (): CachedStateIndexEntry[] => {
  if (!localStorageIsSupported()) return [];

  const cached = getLocalStorageItem(INDEX_STORAGE_KEY);
  if (!cached) return [];

  const parsed = safeParseJson<CachedStateIndexEntry[]>(cached);
  return Array.isArray(parsed) ? parsed : [];
};

const setCachedStateIndex = (entries: CachedStateIndexEntry[]) => {
  setLocalStorageItem(INDEX_STORAGE_KEY, JSON.stringify(entries));
};

const upsertCachedStateIndexEntry = (entry: CachedStateIndexEntry) => {
  const existingEntries = getCachedStateIndex().filter(
    (existingEntry) => existingEntry.storageKey !== entry.storageKey,
  );
  setCachedStateIndex([...existingEntries, entry]);
};

const removeCachedStateIndexEntry = (storageKey: string) => {
  const nextEntries = getCachedStateIndex().filter(
    (entry) => entry.storageKey !== storageKey,
  );
  setCachedStateIndex(nextEntries);
};

const loadFromLocalStorage = <T,>(storageKey: string, initial: T): T => {
  if (!localStorageIsSupported()) return initial;

  const cached = getLocalStorageItem(storageKey);
  if (!cached) return initial;

  const parsed = safeParseJson<T>(cached);
  return parsed ?? initial;
};

const PERSIST_DEBOUNCE_MS = 400;

type PendingPersist = {
  timeoutId: ReturnType<typeof setTimeout>;
  cacheKey: CachedStateKey | undefined;
  data: unknown;
};

const pendingPersists = new Map<string, PendingPersist>();
let persistFlushListenersAttached = false;

const persistValue = (
  storageKey: string,
  cacheKey: CachedStateKey | undefined,
  data: unknown,
) => {
  if (!localStorageIsSupported()) return;

  if (isEmptyish(data)) {
    removeLocalStorageItem(storageKey);
    removeCachedStateIndexEntry(storageKey);
    return;
  }

  setLocalStorageItem(storageKey, JSON.stringify(data));

  if (cacheKey) {
    upsertCachedStateIndexEntry({
      ...normalizeCacheKey(cacheKey),
      storageKey,
      updatedAt: Date.now(),
    });
  }
};

const cancelScheduledPersist = (storageKey: string) => {
  const pending = pendingPersists.get(storageKey);
  if (!pending) return;

  clearTimeout(pending.timeoutId);
  pendingPersists.delete(storageKey);
};

const schedulePersist = (
  storageKey: string,
  cacheKey: CachedStateKey | undefined,
  data: unknown,
) => {
  cancelScheduledPersist(storageKey);

  const timeoutId = setTimeout(() => {
    pendingPersists.delete(storageKey);
    persistValue(storageKey, cacheKey, data);
  }, PERSIST_DEBOUNCE_MS);

  pendingPersists.set(storageKey, { timeoutId, cacheKey, data });
};

const flushScheduledPersist = (storageKey: string) => {
  const pending = pendingPersists.get(storageKey);
  if (!pending) return;

  clearTimeout(pending.timeoutId);
  pendingPersists.delete(storageKey);
  persistValue(storageKey, pending.cacheKey, pending.data);
};

const flushAllScheduledPersists = () => {
  [...pendingPersists.keys()].forEach(flushScheduledPersist);
};

export const flushCachedStatePersists = flushAllScheduledPersists;

const attachPersistFlushListeners = () => {
  if (persistFlushListenersAttached || typeof window === "undefined") {
    return;
  }

  persistFlushListenersAttached = true;
  window.addEventListener("pagehide", flushAllScheduledPersists);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushAllScheduledPersists();
    }
  });
};

export const listCachedState = (
  filter?: CachedStateFilter,
): CachedStateIndexEntry[] => {
  if (!localStorageIsSupported()) return [];

  return getCachedStateIndex()
    .filter((entry) => matchesCacheFilter(entry, filter))
    .sort((a, b) => b.updatedAt - a.updatedAt);
};

export const purgeCachedState = (options?: {
  filter?: CachedStateFilter;
  maxAgeMs?: number;
}) => {
  if (!localStorageIsSupported()) return;

  const now = Date.now();
  const entries = getCachedStateIndex();
  const entriesToRemove = entries.filter((entry) => {
    if (!matchesCacheFilter(entry, options?.filter)) return false;
    if (!options?.maxAgeMs) return true;
    return now - entry.updatedAt >= options.maxAgeMs;
  });

  if (!entriesToRemove.length) return;

  const storageKeysToRemove = new Set(
    entriesToRemove.map((entry) => entry.storageKey),
  );

  entriesToRemove.forEach((entry) => {
    removeLocalStorageItem(entry.storageKey);
  });

  setCachedStateIndex(
    entries.filter((entry) => !storageKeysToRemove.has(entry.storageKey)),
  );
};

export const useCachedState = <T,>(
  key: CachedStateKey | undefined,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const storageKey = useMemo(
    () => (key ? getCachedStateStorageKey(key) : undefined),
    [key],
  );
  const [state, setState] = useState<{ key: string | undefined; data: T }>(
    () => {
      attachPersistFlushListeners();
      const resolvedKey = storageKey;
      if (!resolvedKey) return { key: resolvedKey, data: initial };
      const loaded = loadFromLocalStorage(resolvedKey, initial);
      return { key: resolvedKey, data: loaded };
    },
  );

  const currentData = useMemo(() => {
    if (state.key === storageKey) {
      return state.data;
    }
    if (!storageKey) {
      return initial;
    }
    return loadFromLocalStorage(storageKey, initial);
  }, [initial, state.data, state.key, storageKey]);

  const latestRef = useRef({ storageKey, data: currentData });

  const setCachedState = useCallback<Dispatch<SetStateAction<T>>>(
    (value) => {
      const resolvedStorageKey = storageKey;
      const prev = latestRef.current;
      const prevData =
        prev.storageKey === resolvedStorageKey
          ? prev.data
          : resolvedStorageKey
            ? loadFromLocalStorage(resolvedStorageKey, initial)
            : initial;
      const nextData =
        typeof value === "function"
          ? (value as (prevState: T) => T)(prevData)
          : value;

      latestRef.current = { storageKey: resolvedStorageKey, data: nextData };
      setState({ key: resolvedStorageKey, data: nextData });

      if (!resolvedStorageKey || !localStorageIsSupported()) {
        return;
      }

      // Clears should hit storage immediately so a successful submit cannot
      // be undone by a refresh during the debounce window.
      if (isEmptyish(nextData)) {
        cancelScheduledPersist(resolvedStorageKey);
        persistValue(resolvedStorageKey, key, nextData);
        return;
      }

      schedulePersist(resolvedStorageKey, key, nextData);
    },
    [initial, key, storageKey],
  );

  return [currentData, setCachedState];
};
